import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { useSolveConnectorPlacement } from 'api/queries/forming/forming';
import { buildSolveConnectorPlacementRequest } from 'app/modules/wallforming/components/setting-container/connector-placement/utils/buildSolveConnectorPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { applyPlacementGroups } from 'app/modules/wallforming/components/state-management/placement/utils/applyPlacementGroups';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

interface AutomatedConnectorPlacementProps {
    setLoadingState: Dispatch<SetStateAction<boolean>>;
}

export const useGetAutomatedConnectorPlacement = ({ setLoadingState }: AutomatedConnectorPlacementProps) => {
    const { t } = useTranslation();
    const solveConnectorPlacementMutation = useSolveConnectorPlacement();
    const isPendingRef = useRef<boolean>(false);

    const { selectedPoiIndex } = usePlacementStore(
        useShallow((state) => ({
            selectedPoiIndex: state.poiSelection.current.index
        }))
    );

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(async () => {
        if (selectedPoiIndex === undefined || isPendingRef.current) {
            return;
        }

        isPendingRef.current = true;
        setLoadingState(true);

        try {
            const state = usePlacementStore.getState();
            const payload = buildSolveConnectorPlacementRequest({
                state,
                sessionId: sessionguid,
                systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                poiIndex: selectedPoiIndex
            });
            payload.publishPlacements = false;
            const revision = state.workspaceRevision;

            const response = await solveConnectorPlacementMutation.mutateAsync({
                data: payload
            });

            if (!response.accepted) {
                toastNotifications.error({
                    title: t('error'),
                    message: `${response.errorCode}: ${response.message}`
                });
                return;
            } else {
                if (usePlacementStore.getState().workspaceRevision !== revision)
                    {throw new Error(t('copilotStaleContextError'));}
                applyPlacementGroups(response);
                toastNotifications.success({ title: t('success'), message: t('successConnectorPlacement') });
            }
        } catch (error) {
            toastNotifications.error({
                title: t('error'),
                message: error instanceof Error ? error.message : t('errorAutoConnectorPlacement')
            });
        } finally {
            isPendingRef.current = false;
            setLoadingState(false);
        }
    }, [selectedPoiIndex, sessionguid, setLoadingState, solveConnectorPlacementMutation, t]);
};
